import type { EducationLevel, LocationStatus } from "./types";

// ── Custom BRECCS SVG school markers ───────────────────────────────────────
// Each education level gets a distinct emblem. No emojis, no generic pins.

const LEVEL_COLORS: Record<string, string> = {
  nursery: "#f59e0b",
  primary: "#059669",
  secondary: "#0284c7",
  tertiary: "#7c3aed",
  vocational: "#d97706",
  other: "#64748b",
};

const LEVEL_GLYPH: Record<string, string> = {
  nursery: "N",
  primary: "P",
  secondary: "S",
  tertiary: "T",
  vocational: "V",
  other: "•",
};

export function getSchoolMarkerSVG(
  level: EducationLevel | null,
  locationStatus: LocationStatus | null,
  selected: boolean,
  size: "sm" | "md" | "lg" = "md",
): string {
  const color = LEVEL_COLORS[level ?? "other"] ?? LEVEL_COLORS.other;
  const glyph = LEVEL_GLYPH[level ?? "other"] ?? LEVEL_GLYPH.other;
  const dim = size === "sm" ? { w: 28, h: 36 } : size === "lg" ? { w: 44, h: 56 } : { w: 36, h: 46 };
  const badgeR = dim.w / 2 - 3;
  const cx = dim.w / 2;
  const cy = badgeR + 2;

  const statusDot = locationStatus === "verified"
    ? `<circle cx='${cx + badgeR - 4}' cy='${cy - badgeR + 4}' r='4' fill='#059669' stroke='white' stroke-width='1.5'/>`
    : locationStatus === "needs_verification" || locationStatus === "approximate"
    ? `<circle cx='${cx + badgeR - 4}' cy='${cy - badgeR + 4}' r='4' fill='#f59e0b' stroke='white' stroke-width='1.5'/>`
    : locationStatus === "missing"
    ? `<circle cx='${cx + badgeR - 4}' cy='${cy - badgeR + 4}' r='4' fill='#dc2626' stroke='white' stroke-width='1.5'/>`
    : "";

  const selectedRing = selected
    ? `<circle cx='${cx}' cy='${cy}' r='${badgeR + 4}' fill='none' stroke='${color}' stroke-width='2.5' opacity='0.4'/>`
    : "";

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${dim.w}' height='${dim.h}' viewBox='0 0 ${dim.w} ${dim.h}'>
    <defs>
      <filter id='sh' x='-30%' y='-30%' width='160%' height='160%'>
        <feDropShadow dx='0' dy='2' stdDeviation='2' flood-color='#000' flood-opacity='0.3'/>
      </filter>
    </defs>
    ${selectedRing}
    <path d='M${cx} ${dim.h} L${cx - 5} ${cy + badgeR - 2} L${cx + 5} ${cy + badgeR - 2} Z' fill='${color}' filter='url(#sh)'/>
    <circle cx='${cx}' cy='${cy}' r='${badgeR}' fill='${color}' stroke='white' stroke-width='2' filter='url(#sh)'/>
    <text x='${cx}' y='${cy + 4}' text-anchor='middle' font-family='Inter,sans-serif' font-size='${badgeR * 0.9}' font-weight='700' fill='white'>${glyph}</text>
    ${statusDot}
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function getClusterSVG(count: number): string {
  const w = 52, h = 52;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 ${w} ${h}'>
    <defs>
      <filter id='cs' x='-30%' y='-30%' width='160%' height='160%'>
        <feDropShadow dx='0' dy='2' stdDeviation='3' flood-color='#0c3320' flood-opacity='0.35'/>
      </filter>
    </defs>
    <circle cx='26' cy='26' r='22' fill='#0c3320' stroke='white' stroke-width='2.5' filter='url(#cs)'/>
    <circle cx='26' cy='26' r='18' fill='none' stroke='#1e7a45' stroke-width='1' opacity='0.5'/>
    <text x='26' y='30' text-anchor='middle' font-family='Inter,sans-serif' font-size='13' font-weight='700' fill='white'>${count > 999 ? "999+" : count}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function getUnassignedMarkerSVG(): string {
  const w = 36, h = 46;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 ${w} ${h}'>
    <defs>
      <filter id='us' x='-30%' y='-30%' width='160%' height='160%'>
        <feDropShadow dx='0' dy='2' stdDeviation='2' flood-color='#000' flood-opacity='0.3'/>
      </filter>
    </defs>
    <path d='M18 46 L13 38 L23 38 Z' fill='#dc2626' filter='url(#us)'/>
    <circle cx='18' cy='18' r='15' fill='#dc2626' stroke='white' stroke-width='2' filter='url(#us)'/>
    <path d='M18 10 L18 22 M12 16 L24 16' stroke='white' stroke-width='2.5' stroke-linecap='round'/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export { LEVEL_COLORS as SCHOOL_LEVEL_COLORS };
